import { useState } from "react";
import {
  FieldArray,
  Form,
  Formik,
  useFormikContext,
  type FormikHelpers,
} from "formik";
import { number, object, string } from "yup";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import Button from "../UI/Button";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import PretTable from "./PretTable.tsx";
import styles from "./PretForm.module.css";
import {
  BalanceDomain,
  DOMAINE_ID_SEPARATOR,
} from "../../utils/constants/balances";
import type {
  EvenementType,
  ModeDiffere,
  TableauAmortissement,
  TableauAmortissementPreview,
} from "../../models/tableauAmortissement";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import IconButton from "../UI/IconButton.tsx";

interface EventFormValues {
  type: EvenementType;
  date: string;
  montant?: string | number | null;
  taux?: string | number | null;
  mode?: ModeDiffere | null;
  date_fin?: string | null;
  facteur?: string | number | null;
}

interface PretFormValues {
  nom: string;
  affectation: string;
  date_debut: string;
  duree_mois: string | number;
  montant: string | number;
  taux: string | number;
  taux_assurance: string | number;
  frais_dossier: string | number;
  evenements: EventFormValues[];
}

interface PretBaseValues {
  montant: string | number;
  taux: string | number;
  taux_assurance: string | number;
  frais_dossier: string | number;
}

interface CleanedEvent {
  type: EvenementType;
  date: string;
  montant?: number;
  taux?: number;
  date_fin?: string;
  mode?: ModeDiffere;
  facteur?: number;
}

const EVENT_TYPES: { value: EvenementType; label: string }[] = [
  { value: "deblocage", label: "Déblocage supplémentaire" },
  { value: "taux", label: "Changement de taux" },
  { value: "assurance", label: "Changement d'assurance" },
  { value: "differe", label: "Différé" },
  { value: "suspension", label: "Suspension" },
];

const DEFAULT_EVENT: Record<string, EventFormValues> = {
  deblocage: { type: "deblocage", date: "", montant: "" },
  taux: { type: "taux", date: "", taux: "", montant: "" },
  assurance: { type: "assurance", date: "", taux: "" },
  differe: { type: "differe", date: "", date_fin: "", mode: "partiel" },
  suspension: { type: "suspension", date: "", date_fin: "" },
};

const validationSchema = object({
  nom: string().required("Le nom est requis"),
  affectation: string().required("L'affectation est requise"),
  date_debut: string().required("La date de début est requise"),
  duree_mois: number()
    .required("La durée est requise")
    .min(1, "Minimum 1 mois"),
  montant: number().required("Le montant est requis").min(1, "Minimum 1 EUR"),
  taux: number().required("Le taux est requis").min(0, "Minimum 0%"),
  taux_assurance: number().min(0, "Minimum 0%"),
});

function formatAffectation(
  domaine: string,
  parentId: number | string | null,
): string {
  if (!domaine || !parentId) return "";
  return `${domaine}${DOMAINE_ID_SEPARATOR}${parentId}`;
}

function parseAffectation(affectation: string) {
  if (!affectation)
    return { domaine: "", id_bien: null, id_property_folder: null };
  const [domaine, parentId] = affectation.split(DOMAINE_ID_SEPARATOR);
  return {
    domaine,
    id_bien: domaine === BalanceDomain.PROPERTY ? parseInt(parentId) : null,
    id_property_folder:
      domaine === BalanceDomain.FOLDER ? parseInt(parentId) : null,
  };
}

function getInitialAffectation(
  initialData?: TableauAmortissement | null,
): string {
  if (!initialData?.domaine) return "";
  if (initialData.domaine === BalanceDomain.PROPERTY && initialData.id_bien) {
    return formatAffectation(BalanceDomain.PROPERTY, initialData.id_bien);
  }
  if (
    initialData.domaine === BalanceDomain.FOLDER &&
    initialData.id_property_folder
  ) {
    return formatAffectation(
      BalanceDomain.FOLDER,
      initialData.id_property_folder,
    );
  }
  return "";
}

function extractInitialValues(
  initialData?: TableauAmortissement | null,
): PretBaseValues {
  if (!initialData?.evenements)
    return { montant: "", taux: "", taux_assurance: "", frais_dossier: "" };

  const deblocage = initialData.evenements.find((e) => e.type === "deblocage");
  const taux = initialData.evenements.find((e) => e.type === "taux");
  const assurance = initialData.evenements.find((e) => e.type === "assurance");
  const frais = initialData.evenements.find((e) => e.type === "frais_dossier");

  return {
    montant: deblocage?.montant || "",
    taux: taux?.taux || "",
    taux_assurance: assurance?.taux || "",
    frais_dossier: frais?.montant || "",
  };
}

function filterExtraEvents(evenements?: EventFormValues[]): EventFormValues[] {
  if (!evenements) return [];
  let foundDeblocage = false,
    foundTaux = false,
    foundAssurance = false,
    foundFrais = false;
  return evenements.filter((e) => {
    if (e.type === "deblocage" && !foundDeblocage) {
      foundDeblocage = true;
      return false;
    }
    if (e.type === "taux" && !foundTaux) {
      foundTaux = true;
      return false;
    }
    if (e.type === "assurance" && !foundAssurance) {
      foundAssurance = true;
      return false;
    }
    if (e.type === "frais_dossier" && !foundFrais) {
      foundFrais = true;
      return false;
    }
    return true;
  });
}

function buildEventsList(values: PretFormValues): CleanedEvent[] {
  const evenements: CleanedEvent[] = [];

  if (values.montant && values.date_debut) {
    evenements.push({
      type: "deblocage",
      date: values.date_debut,
      montant: parseFloat(String(values.montant)),
    });
  }

  if (values.taux && values.date_debut) {
    evenements.push({
      type: "taux",
      date: values.date_debut,
      taux: parseFloat(String(values.taux)),
    });
  }

  if (values.taux_assurance && values.date_debut) {
    evenements.push({
      type: "assurance",
      date: values.date_debut,
      taux: parseFloat(String(values.taux_assurance)),
    });
  }

  if (values.frais_dossier && values.date_debut) {
    evenements.push({
      type: "frais_dossier",
      date: values.date_debut,
      montant: parseFloat(String(values.frais_dossier)),
    });
  }

  values.evenements.forEach((evt) => {
    if (!evt.date) return;
    const cleaned: CleanedEvent = { type: evt.type, date: evt.date };
    if (evt.montant) cleaned.montant = parseFloat(String(evt.montant));
    if (evt.taux) cleaned.taux = parseFloat(String(evt.taux));
    if (evt.date_fin) cleaned.date_fin = evt.date_fin;
    if (evt.mode) cleaned.mode = evt.mode;
    if (evt.facteur) cleaned.facteur = parseFloat(String(evt.facteur));
    evenements.push(cleaned);
  });

  return evenements;
}

interface EventFieldsProps {
  index: number;
  event: EventFormValues;
}

function EventFields({ index, event }: EventFieldsProps) {
  const prefix = `evenements[${index}]`;

  const commonDateField = (
    <TextInput
      type="date"
      name={`${prefix}.date`}
      label="Date"
      className={styles.eventField}
      marginForm=""
    />
  );

  switch (event.type) {
    case "deblocage":
      return (
        <>
          {commonDateField}
          <TextInput
            type="number"
            name={`${prefix}.montant`}
            label="Montant (EUR)"
            placeholder="100000"
            className={styles.eventField}
            marginForm=""
          />
        </>
      );

    case "taux":
      return (
        <>
          {commonDateField}
          <TextInput
            type="number"
            step="0.01"
            name={`${prefix}.taux`}
            label="Taux (%)"
            placeholder="2.5"
            className={styles.eventField}
            marginForm=""
          />
          <TextInput
            type="number"
            name={`${prefix}.montant`}
            label="Montant tranche"
            placeholder="Optionnel"
            className={styles.eventField}
            marginForm=""
          />
        </>
      );

    case "assurance":
      return (
        <>
          {commonDateField}
          <TextInput
            type="number"
            step="0.01"
            name={`${prefix}.taux`}
            label="Taux annuel (%)"
            placeholder="0.36"
            className={styles.eventField}
            marginForm=""
          />
        </>
      );

    case "differe":
      return (
        <>
          {commonDateField}
          <TextInput
            type="date"
            name={`${prefix}.date_fin`}
            label="Date fin"
            className={styles.eventField}
            marginForm=""
          />
          <ReactSelect
            name={`${prefix}.mode`}
            label="Type"
            className={styles.eventField}
            isClearable={false}
            options={[
              { value: "partiel", label: "Partiel (intérêts seuls)" },
              { value: "total", label: "Total (aucun paiement)" },
            ]}
            marginForm=""
          />
        </>
      );

    case "suspension":
      return (
        <>
          {commonDateField}
          <TextInput
            type="date"
            name={`${prefix}.date_fin`}
            label="Date fin"
            className={styles.eventField}
            marginForm=""
          />
        </>
      );

    default:
      return commonDateField;
  }
}

interface InnerFormProps {
  profileBailleurId: number;
  onClose: () => void;
  previewData: TableauAmortissementPreview | null;
  setPreviewData: (data: TableauAmortissementPreview | null) => void;
  setError: (error: string) => void;
}

function InnerForm({
  profileBailleurId,
  onClose,
  previewData,
  setPreviewData,
  setError,
}: InnerFormProps) {
  const axios = useAuthenticatedAxios();
  const { properties } = useProperties();
  const { propertyFolders } = usePropertyFolders();
  const { values, validateForm, setTouched } =
    useFormikContext<PretFormValues>();
  const [isPreviewing, setIsPreviewing] = useState(false);

  const filteredProperties = properties.filter(
    (p: { id_profile_nom_owner: number }) =>
      p.id_profile_nom_owner === profileBailleurId,
  );
  const filteredFolders = propertyFolders.filter(
    (f: { id_profile_nom_owner: number }) =>
      f.id_profile_nom_owner === profileBailleurId,
  );

  const affectationOptions = [
    {
      label: "Dossiers",
      options: filteredFolders.map((folder: { id: number; nom: string }) => ({
        label: folder.nom,
        value: formatAffectation(BalanceDomain.FOLDER, folder.id),
      })),
    },
    {
      label: "Biens",
      options: filteredProperties.map((prop: { id: number; nom: string }) => ({
        label: prop.nom,
        value: formatAffectation(BalanceDomain.PROPERTY, prop.id),
      })),
    },
  ];

  async function handlePreview() {
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      );
      return;
    }

    setError("");
    setIsPreviewing(true);

    try {
      const data = {
        date_debut: values.date_debut,
        duree_mois: parseInt(String(values.duree_mois)),
        evenements: buildEventsList(values),
      };

      const response = await axios.post("tableaux-amortissement/preview", data);
      setPreviewData(response.data);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setIsPreviewing(false);
    }
  }

  return (
    <Form>
      <ModalDialogBody>
        <div className={styles.section}>
          <Title variant="kilo">Informations générales</Title>
          <div className={styles.formGrid}>
            <TextInput
              name="nom"
              label="Nom du prêt"
              placeholder="Prêt appartement Paris"
              marginForm=""
            />
            <ReactSelect
              name="affectation"
              label="Affecter à"
              options={affectationOptions}
              placeholder="Choisir un bien ou dossier..."
              isClearable={false}
              marginForm=""
            />
            <TextInput
              type="date"
              name="date_debut"
              label="Date de début"
              marginForm=""
            />
            <TextInput
              type="number"
              name="duree_mois"
              label="Durée (mois)"
              placeholder="240"
              marginForm=""
            />
          </div>
        </div>

        <div className={styles.section}>
          <Title variant="kilo">Paramètres du prêt</Title>
          <div className={styles.formGrid}>
            <TextInput
              type="number"
              name="montant"
              label="Montant emprunté (EUR)"
              placeholder="200000"
              marginForm=""
            />
            <TextInput
              type="number"
              step="0.01"
              name="taux"
              label="Taux d'intérêt annuel (%)"
              placeholder="2.5"
              marginForm=""
            />
            <TextInput
              type="number"
              step="0.01"
              name="taux_assurance"
              label="Taux assurance annuel (%)"
              placeholder="0.36"
              marginForm=""
            />
            <TextInput
              type="number"
              step="0.01"
              name="frais_dossier"
              label="Frais de dossier (EUR)"
              placeholder="0"
              marginForm=""
            />
          </div>
        </div>

        <div className={styles.section}>
          <Title variant="kilo">Évènements supplémentaires</Title>
          <Text color="muted">
            Ajoutez des évènements si le prêt a des particularités : déblocages
            multiples (travaux), changement de taux, différé, suspension, etc.
          </Text>

          <FieldArray name="evenements">
            {({ push, remove }) => (
              <>
                <div className={styles.addEventButtons}>
                  {EVENT_TYPES.map((type) => (
                    <Button
                      key={type.value}
                      type="button"
                      variant="secondary"
                      onClick={() =>
                        push({
                          ...DEFAULT_EVENT[type.value],
                          date: values.date_debut,
                        })
                      }
                      icon={IconPlus}
                    >
                      {type.label}
                    </Button>
                  ))}
                </div>

                <div className={styles.eventsList}>
                  {values.evenements.map((event, index) => (
                    <Card key={index} className={styles.eventCard}>
                      <div className={styles.eventHeader}>
                        <Text as="span" weight="bold">
                          {EVENT_TYPES.find((t) => t.value === event.type)
                            ?.label || event.type}
                        </Text>
                        <IconButton
                          onClick={() => remove(index)}
                          label={"Supprimer"}
                          icon={IconTrash}
                        />
                      </div>
                      <div className={styles.eventFields}>
                        <EventFields index={index} event={event} />
                      </div>
                    </Card>
                  ))}
                </div>

                {values.evenements.length === 0 && (
                  <Card padding="sm" className="text-center">
                    <Text color="muted">
                      Aucun évènement supplémentaire. Le prêt sera calculé avec
                      les paramètres de base.
                    </Text>
                  </Card>
                )}
              </>
            )}
          </FieldArray>
        </div>

        {previewData && (
          <div className={styles.section}>
            <Title variant="kilo">Aperçu du tableau d'amortissement</Title>
            <Card padding="sm" className={styles.previewStats}>
              <div>
                <Text as="span">Montant total : </Text>
                <Text as="span" weight="bold">
                  {currencyFormatter(previewData.montant_total)}
                </Text>
              </div>
              <div>
                <Text as="span">Nombre d'échéances : </Text>
                <Text as="span" weight="bold">
                  {previewData.contenu?.length || 0}
                </Text>
              </div>
            </Card>
            <PretTable lignes={previewData.contenu} isPreview />
          </div>
        )}
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button
          variant="secondary"
          onClick={handlePreview}
          isLoading={isPreviewing}
        >
          Prévisualiser
        </Button>
        <FormikSubmitButton>Enregistrer</FormikSubmitButton>
      </ModalDialogFooter>
    </Form>
  );
}

interface PretFormProps {
  profileBailleurId: number;
  initialData?: TableauAmortissement | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: Partial<TableauAmortissement>) => void | Promise<void>;
}

export default function PretForm({
  profileBailleurId,
  initialData = null,
  open,
  onOpenChange,
  onSubmit,
}: PretFormProps) {
  const [error, setError] = useState("");
  const [previewData, setPreviewData] =
    useState<TableauAmortissementPreview | null>(null);

  const initialValues = extractInitialValues(initialData);

  const formInitialValues: PretFormValues = {
    nom: initialData?.nom || "",
    affectation: getInitialAffectation(initialData),
    date_debut: initialData?.date_debut || "",
    duree_mois: initialData?.duree_mois || "",
    montant: initialValues.montant,
    taux: initialValues.taux,
    taux_assurance: initialValues.taux_assurance || "",
    frais_dossier: initialValues.frais_dossier || "",
    evenements: filterExtraEvents(initialData?.evenements),
  };

  async function handleSubmit(
    values: PretFormValues,
    { setSubmitting }: FormikHelpers<PretFormValues>,
  ) {
    setError("");

    try {
      const { domaine, id_bien, id_property_folder } = parseAffectation(
        values.affectation,
      );

      const data = {
        nom: values.nom,
        domaine,
        id_bien,
        id_property_folder,
        date_debut: values.date_debut,
        duree_mois: parseInt(String(values.duree_mois)),
        evenements: buildEventsList(values),
      };

      await onSubmit(data);
      onOpenChange(false);
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ resetForm }) => (
        <ModalDialog
          open={open}
          size="lg"
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setError("");
              setPreviewData(null);
            }
            onOpenChange(nextOpen);
          }}
          header={
            <Title variant="mega">
              {initialData ? "Modifier le prêt" : "Nouveau prêt"}
            </Title>
          }
        >
          <Callout variant="danger" className={styles.callout}>
            {error}
          </Callout>
          <InnerForm
            profileBailleurId={profileBailleurId}
            onClose={() => onOpenChange(false)}
            previewData={previewData}
            setPreviewData={setPreviewData}
            setError={setError}
          />
        </ModalDialog>
      )}
    </Formik>
  );
}
